import React, { useRef, useState } from 'react';
import type { TaskItem } from '../../../types/topic';
import { TaskFormTitle } from './TaskFormTitle';
import { TaskFormDescription } from './TaskFormDescription';
import { TaskFormDeadline } from './TaskFormDeadline';
import { TagEditor } from './TagEditor';
import { TaskAssigneePicker } from './TaskAssigneePicker';
import { logger } from '../../../utils/logger';
import { Check } from 'lucide-react';

interface TaskFormEditProps {
  task: TaskItem;
  projectId?: string;
  availableTags?: string[];
  onSubmit: (task: TaskItem) => void;
  onChange: (task: TaskItem) => void;
  onCancel: () => void;
  hasCollaborators?: boolean;
  hideButtons?: boolean;
}

export const TaskFormEdit: React.FC<TaskFormEditProps> = ({
  task,
  projectId,
  availableTags = [],
  onSubmit,
  onChange,
  onCancel,
  hasCollaborators = false,
  hideButtons = false,
}) => {
  // State management for task fields
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDescription, setEditDescription] = useState(task.description || '');
  const [editDeadline, setEditDeadline] = useState<Date | null>(
    task.deadline ? new Date(task.deadline) : null
  );
  const [editTags, setEditTags] = useState<string[]>(task.tags || []);
  // Handle both snake_case and camelCase versions based on data source
  const assignedTo = task.assignedTo !== undefined ? task.assignedTo : task.assigned_to;
  const [editAssignedTo, setEditAssignedTo] = useState<string | null>(assignedTo || null);

  // Refs for preventing update loops
  const isUpdatingRef = useRef(false);
  const isUserTypingRef = useRef(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const lastDeadlineRef = useRef<string | null>(null);

  // Handle form submission
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Get any pending tag from the input field
    const pendingTagInput = tagInputRef.current;
    const finalTags = [...editTags];

    if (pendingTagInput && pendingTagInput.value.trim()) {
      const pendingTag = pendingTagInput.value.trim();
      if (!editTags.includes(pendingTag)) {
        finalTags.push(pendingTag);
      }
    }

    const updatedTask = {
      ...task,
      title: editTitle,
      description: editDescription || null,
      deadline: editDeadline ? editDeadline.toISOString() : null,
      tags: finalTags.length > 0 ? finalTags : null,
      // Use snake_case format for the backend
      assigned_to: editAssignedTo,
    };
    
    onSubmit(updatedTask);
  };

  return (
    <div ref={formRef} className="rounded-[5px] transition-all relative text-m">
      {/* Title field */}
      <TaskFormTitle 
        value={editTitle}
        onChange={(newTitle) => {
          setEditTitle(newTitle);

          // Report change immediately to parent component if in edit mode
          if (task && onChange && !isUpdatingRef.current) {
            isUpdatingRef.current = true;
            try {
              onChange({
                ...task,
                title: newTitle,
                description: editDescription || null,
                deadline: editDeadline ? editDeadline.toISOString() : null,
                tags: editTags.length > 0 ? editTags : null,
                // Use snake_case format for the backend
                assigned_to: editAssignedTo,
              });
            } finally {
              // Use setTimeout to ensure we break any potential circular update chain
              setTimeout(() => {
                isUpdatingRef.current = false;
              }, 0);
            }
          }
        }}
        isEditMode={true}
        inputRef={titleRef}
      />

      {/* Separator line */}
      <div className="w-full h-[1px] bg-gray-200 mb-6"></div>

      {/* Description field */}
      <TaskFormDescription
        value={editDescription}
        onChange={(newDescription) => {
          setEditDescription(newDescription);

          // Report change immediately to parent component if in edit mode
          if (task && onChange && !isUpdatingRef.current) {
            isUpdatingRef.current = true;
            try {
              onChange({
                ...task,
                title: editTitle,
                description: newDescription || null,
                deadline: editDeadline ? editDeadline.toISOString() : null,
                tags: editTags.length > 0 ? editTags : null,
                // Use snake_case format for the backend
                assigned_to: editAssignedTo,
              });
            } finally {
              // Use setTimeout to ensure we break any potential circular update chain
              setTimeout(() => {
                isUpdatingRef.current = false;
              }, 0);
            }
          }
        }}
        isEditMode={true}
      />

      {/* Deadline picker */}
      <div className="relative mt-4">
        <TaskFormDeadline
          selectedDate={editDeadline}
          onDateChange={(date) => {
            // Create a fresh Date object to avoid any reference issues
            const newDate = date ? new Date(date.getTime()) : null;
            if (newDate) {
              // Set hours to noon to avoid timezone issues
              newDate.setHours(12, 0, 0, 0);
            }
            
            // Update local state
            setEditDeadline(newDate);
            
            // Track the new deadline value to prevent infinite loops
            lastDeadlineRef.current = newDate ? newDate.toISOString() : null;
            
            // Notify parent component of change
            if (task && onChange && !isUpdatingRef.current) {
              isUpdatingRef.current = true;
              try {
                const updatedTask = {
                  ...task,
                  title: editTitle,
                  description: editDescription || null,
                  deadline: newDate ? newDate.toISOString() : null,
                  tags: editTags.length > 0 ? editTags : null,
                  // Use snake_case format for the backend
                assigned_to: editAssignedTo,
                };
                
                logger.component('TaskForm updating task with new date', {
                  taskId: task.id,
                  deadline: newDate ? newDate.toISOString() : null,
                });
                
                onChange(updatedTask);
              } finally {
                // Use setTimeout to ensure we break any potential circular update chain
                setTimeout(() => {
                  isUpdatingRef.current = false;
                }, 0);
              }
            }
          }}
          taskId={task.id}
          taskStatus={task.status}
          isEditMode={true}
        />
      </div>

      {/* Tags section */}
      <div className="mt-10">
        <TagEditor
          tags={editTags}
          onTagsChange={(newTags) => {
            setEditTags(newTags);
            
            // Only notify changes if this wasn't triggered by the useEffect
            if (task && onChange && !isUserTypingRef.current && !isUpdatingRef.current) {
              isUpdatingRef.current = true;
              try {
                onChange({
                  ...task,
                  title: editTitle,
                  description: editDescription || null,
                  deadline: editDeadline ? editDeadline.toISOString() : null,
                  tags: newTags.length > 0 ? newTags : null,
                  // Use snake_case format for the backend
                assigned_to: editAssignedTo,
                });
              } finally {
                // Use setTimeout to ensure we break any potential circular update chain
                setTimeout(() => {
                  isUpdatingRef.current = false;
                }, 0);
              }
            }
            
            // Reset the flag
            isUserTypingRef.current = false;
          }}
          status={task.status}
          availableTags={availableTags}
          inputRef={tagInputRef}
          isNewForm={false}
        />
      </div>

      {/* Assignee Picker */}
      {hasCollaborators && (
        <div className="relative mt-10">
          <TaskAssigneePicker
            assignedUserId={editAssignedTo}
            projectId={projectId}
            onAssigneeChange={(userId) => {
              logger.component('TaskForm: Assignee changed', {
                from: editAssignedTo || 'null',
                to: userId || 'null',
                isEditMode: true,
                projectId,
              });

              // Update the local state to show the selection immediately
              setEditAssignedTo(userId);

              // In edit mode, update parent component state
              if (task && onChange && !isUpdatingRef.current) {
                isUpdatingRef.current = true;
                try {
                  onChange({
                    ...task,
                    title: editTitle,
                    description: editDescription || null,
                    deadline: editDeadline ? editDeadline.toISOString() : null,
                    tags: editTags.length > 0 ? editTags : null,
                    assigned_to: userId,
                  });
                } finally {
                  // Use setTimeout to ensure we break any potential circular update chain
                  setTimeout(() => {
                    isUpdatingRef.current = false;
                  }, 0);
                }
              }
            }}
          />
        </div>
      )}

      {/* Control buttons */}
      {!hideButtons && (
        <div className="flex justify-end mt-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={e => {
                // Stop propagation to prevent parent elements from capturing this event
                e.stopPropagation();

                logger.component('TaskForm: Cancel button clicked (edit mode)', {
                  isEditMode: true,
                  taskId: task?.id,
                });

                // Call the onCancel callback
                onCancel();
              }}
              className="flex items-center px-2 py-1 text-gray-600 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={e => {
                // Stop propagation to prevent parent elements from capturing this event
                e.stopPropagation();
                handleEditSubmit(e);
              }}
              className="flex items-center px-3 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm font-medium transition-colors"
            >
              <Check size={14} className="flex-shrink-0 mr-1.5" />
              <span>Save Task</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};